import { useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";

/**
 * App open tracking — heartbeat hook (PR 2 do refator de tracking real).
 *
 * Substitui proxy `ultima_atividade = min(updated_at, feedback_*)` por dado
 * fidedigno de "abriu o app". Plugado UMA vez em App.tsx (chamada global).
 *
 * Triggers:
 *   1. Mount inicial (cold start)
 *   2. visibilitychange → visible (volta de background, app switching)
 *   3. pageshow com event.persisted=true (BFCache iOS — não dispara em
 *      navigation comum)
 *
 * Defesas:
 *   - Aguarda a sessão compartilhada resolver ANTES de registrar listeners (evita
 *     race onde listeners disparam pre-auth e debounce gravado mascara o
 *     primeiro heartbeat real pós-login)
 *   - Debounce 30min via localStorage["ondiet-last-heartbeat-at"] (hyphen
 *     alinha com convenção dominante de hooks no projeto)
 *   - Optimistic write do debounce ANTES do fetch (protege race mount +
 *     visibilitychange no mesmo tick)
 *   - Try/catch em localStorage (private mode webview pode lançar
 *     QuotaExceededError)
 *   - keepalive: true no fetch (envia mesmo se app fechar logo após).
 *     Limite de 64KB body — body é ~25 bytes, não-issue.
 *   - Silent failure (try/catch + console.warn) — tracking nunca bloqueia UX
 *
 * Plano histórico não normativo: legal-faz-seu-self-review-mutable-nova.md
 */

const API_URL = (import.meta.env.VITE_API_URL as string) || "https://api.ondiet.app";
const HEARTBEAT_DEBOUNCE_MS = 30 * 60 * 1000; // 30min
const STORAGE_KEY = "ondiet-last-heartbeat-at";

function getLastHeartbeatAt(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? parseInt(stored, 10) || 0 : 0;
  } catch {
    // Private mode webview pode lançar — fallback = 0 (sempre dispara).
    // Server-side debounce de 5min protege backend nesse cenário.
    return 0;
  }
}

function setLastHeartbeatAt(ts: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(ts));
  } catch {
    // QuotaExceededError ou private mode — silent. Pode disparar duplicado
    // dentro do debounce window, server-side debounce protege.
  }
}

async function sendHeartbeat(accessToken: string): Promise<void> {
  const now = Date.now();
  if (now - getLastHeartbeatAt() < HEARTBEAT_DEBOUNCE_MS) {
    return; // Debounce ativo
  }

  try {
    const res = await fetch(`${API_URL}/api/usage/heartbeat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ source: "heartbeat" }),
      keepalive: true,
    });
    // Commit debounce APENAS após sucesso de rede.
    // Sem isso (optimistic write), network failure transitória bloquearia
    // heartbeats por 30min — user offline + reabre app = heartbeat morto.
    // Race window mount+visibilitychange é mitigado pelo debounce do servidor;
    // o hook não disputa mais o lock do Supabase Auth para reler a sessão.
    if (res.ok) {
      setLastHeartbeatAt(now);
    }
  } catch (err) {
    // Silent failure — não interrompe UX. Debounce não foi commitado,
    // próxima tentativa pode disparar (server-side debounce de 5min protege).
    console.warn("[heartbeat] failed silently:", err);
  }
}

export function useAppHeartbeat(): void {
  const { session } = useAuth();
  const tokenRef = useRef<string | null>(session?.access_token ?? null);
  tokenRef.current = session?.access_token ?? null;

  useEffect(() => {
    const token = tokenRef.current;
    if (!token) return;

    void sendHeartbeat(token);

    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      const currentToken = tokenRef.current;
      if (currentToken) void sendHeartbeat(currentToken);
    };

    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      const currentToken = tokenRef.current;
      if (currentToken) void sendHeartbeat(currentToken);
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pageshow", onPageShow);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [session?.user.id]);
}
